// Forum réservé aux profils certifiés — créer un sujet ou répondre exige une certification validée
// (les abonnés et le staff en sont exemptés). Miroir de
// services/forumCertification/forumCertification.service.js (voissa-api-rest).
//
// RÉPARTITION DES RÔLES — l'API sert le drapeau global ET le verdict d'exemption dans `currentUser`
// (`forumCertification`, cf. GET /users/me) ; ce module ne fait que les lire. Aucune règle métier
// n'est donc réécrite ici : ajouter une exemption côté API suffit, le front suit sans qu'on touche
// à ce fichier. C'est la leçon tirée du couvre-feu, dont le premier jet dupliquait la règle.
//
// L'API reste seule juge : elle refuse en 403 avec le code ci-dessous, et le front n'anticipe ce
// refus que pour ouvrir la bonne modale — celle de la certification, gratuite, et surtout pas
// celle de l'abonnement.

// Contrat avec l'API : `errors.code` du payload 403.
export const FORUM_CERTIFICATION_ERROR_CODE = 'FORUM_CERTIFICATION_REQUIRED';

// Repli utilisé UNIQUEMENT tant que le payload de l'API n'est pas là : première frame (GET
// /users/me en vol), session ouverte avant la mise en service, front déployé avant l'API.
// `enabled: false` veut dire « sans information, on ne bloque pas » — l'API tranchera et le filet
// global ouvrira la modale sur son refus.
const FORUM_CERTIFICATION_FALLBACK = {
    enabled: false,
    exempt: false,
};

export const getForumCertificationConfig = (currentUser) => ({
    ...FORUM_CERTIFICATION_FALLBACK,
    ...(currentUser?.forumCertification || {}),
});

/**
 * Le membre est-il empêché de publier sur le forum ? (`exempt` vient du serveur.)
 *
 * Un INVITÉ n'est jamais « bloqué » au sens de cette règle : son parcours est l'inscription, pas la
 * certification. Les CTA du forum le renvoient déjà vers la modale de connexion via `<CtaCannot>`,
 * et lui servir « certifiez votre profil » avant même qu'il ait un compte n'aurait aucun sens.
 */
export const isForumCertificationBlocked = (currentUser) => {
    if (!currentUser?._id) return false;
    const config = getForumCertificationConfig(currentUser);
    return config.enabled && !config.exempt;
};

/**
 * Reconnaît le refus de l'API, quelle que soit la forme de l'erreur reçue : mutation
 * (`error.response.errors`), query (`error.errors`) ou axios brut (`error.response.data`).
 * Cf. src/interceptors/data.js, qui normalise différemment les deux premiers cas.
 */
export const isForumCertificationError = (error) => {
    const codes = [
        error?.errors?.code,
        error?.response?.errors?.code,
        error?.response?.data?.errors?.code,
    ];
    return codes.includes(FORUM_CERTIFICATION_ERROR_CODE);
};

// --- Filet de sécurité global -----------------------------------------------------------------
// Tous les chemins de publication du forum ne sont pas gatés au clic (uploads d'images depuis
// l'éditeur, brouillon envoyé depuis un onglet dont le cache `currentUser` date d'avant une
// révocation…). Quand l'API refuse malgré tout, le MutationCache émet ici et le listener monté sous
// le ModalManager ouvre la même modale. Un abonné module-level : l'émetteur (cache React Query) vit
// hors de l'arbre React et n'a pas accès au contexte des modales.
let listeners = [];

export const subscribeForumCertification = (listener) => {
    listeners = [...listeners, listener];
    return () => {
        listeners = listeners.filter((item) => item !== listener);
    };
};

export const emitForumCertification = () => {
    listeners.forEach((listener) => listener());
};
